// src/api/categoryApi.tsx

// -----------------------------
// TYPES
// -----------------------------
import { ResponseMessage } from "../models/ResponseMessage";

import {Constants} from "../constants/constants";
const API_BASE_URL = Constants.API_BASE_URL;

export interface CategoryDto {
  id: string;
  name: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
}

export interface CategoryResDto {
  id: string;
  restaurantId: string;
  restaurantName: string;
  name: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
}

export interface CategoryCreateDto {
  name: string;
  description?: string;
  displayOrder?: number;
  isActive?: boolean;
}

export interface CategoryUpdateDto {
  name?: string;
  description?: string;
  displayOrder?: number;
  isActive?: boolean;
}

// -----------------------------
// BASE CONFIG
// -----------------------------

const BASE_URL = Constants.API_BASE_URL+"/category";

// helper to inject token
const getHeaders = (token?: string) => ({
  "Content-Type": "application/json",
  ...(token && { Authorization: `Bearer ${token}` }),
});

// -----------------------------
// API METHODS
// -----------------------------

// ✅ Get categories by restaurant
export const getCategories = async (
  restaurantId: string,
  token?: string,
): Promise<ResponseMessage<CategoryDto[]>> => {
  const res = await fetch(`${BASE_URL}/restaurants/${restaurantId}`, {
    method: "GET",
    headers: getHeaders(token),
  });

  return res.json();
};

// ✅ Get single category
export const getCategory = async (
  categoryId: string,
  token?: string,
): Promise<ResponseMessage<CategoryDto>> => {
  const res = await fetch(`${BASE_URL}/${categoryId}`, {
    method: "GET",
    headers: getHeaders(token),
  });

  return res.json();
};

// ✅ Create category
export const createCategory = async (
  restaurantId: string,
  data: CategoryCreateDto,
  token: string,
): Promise<ResponseMessage<CategoryDto>> => {
  const res = await fetch(`${BASE_URL}/restaurants/${restaurantId}`, {
    method: "POST",
    headers: getHeaders(token),
    body: JSON.stringify(data),
  });

  return res.json();
};

// ✅ Update category
export const updateCategory = async (
  restaurantId: string,
  categoryId: string,
  data: CategoryUpdateDto,
  token: string,
): Promise<ResponseMessage<boolean>> => {
  const res = await fetch(
    `${BASE_URL}/${categoryId}?restaurantId=${restaurantId}`,
    {
      method: "PUT",
      headers: getHeaders(token),
      body: JSON.stringify(data),
    },
  );

  return res.json();
};

// ✅ Delete category
export const deleteCategory = async (
  restaurantId: string,
  categoryId: string,
  token: string,
): Promise<ResponseMessage<boolean>> => {
  const res = await fetch(
    `${BASE_URL}/${categoryId}?restaurantId=${restaurantId}`,
    {
      method: "DELETE",
      headers: getHeaders(token),
    },
  );

  return res.json();
};

// ✅ Reorder categories
export const reorderCategories = async (
  restaurantId: string,
  categoryOrders: Record<string, number>,
  token: string,
): Promise<ResponseMessage<boolean>> => {
  const res = await fetch(`${BASE_URL}/reorder?restaurantId=${restaurantId}`, {
    method: "PATCH",
    headers: getHeaders(token),
    body: JSON.stringify({ categoryOrders }),
  });

  return res.json();
};

// ✅ Get categories by owner
export const getCategoriesByOwner = async (
  ownerId: string,
  token: string,
): Promise<ResponseMessage<CategoryResDto[]>> => {
  const res = await fetch(`${BASE_URL}/owner/${ownerId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return res.json();
};
